// Chemin: C:\gestion-copro\src\components\admin\PendingUsersSection.tsx
// Section validation inscriptions - ORION PILOT
// Session 8 : utilise firebaseUser (pas user)
// Session 81 : version compactée - une ligne par inscription, dépliable

import React, { useState, useEffect } from 'react';
import { 
  PendingUser, 
  LOT_TYPE_LABELS, 
  ETAGE_LABELS 
} from '../../types/user.types';
import { 
  getPendingUsers, 
  validateUser, 
  rejectUser 
} from '../../services/inscription.service';
import { useAuth } from '../../context/AuthContext';

const PendingUsersSection: React.FC = () => {
  const { firebaseUser } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      const users = await getPendingUsers();
      setPendingUsers(users);
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingUsers();
  }, []);

  // Auto-clear messages après 3s
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleValidate = async (e: React.MouseEvent, pendingUser: PendingUser) => {
    e.stopPropagation(); // Empêcher le toggle expand
    if (!firebaseUser) return;
    
    setProcessing(pendingUser.id);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await validateUser(pendingUser.id, firebaseUser.uid);
      if (result.success) {
        setSuccessMessage(`${pendingUser.prenom} ${pendingUser.nom} validé`);
        await loadPendingUsers();
      } else {
        setError(result.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur lors de la validation');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectModal = (e: React.MouseEvent, pendingUser: PendingUser) => {
    e.stopPropagation(); // Empêcher le toggle expand
    setSelectedUser(pendingUser);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!firebaseUser || !selectedUser) return;
    
    setProcessing(selectedUser.id);

    try {
      const result = await rejectUser(selectedUser.id, firebaseUser.uid, rejectReason);
      if (result.success) {
        setSuccessMessage(`Inscription de ${selectedUser.prenom} ${selectedUser.nom} refusée`);
        setRejectModalOpen(false);
        setSelectedUser(null);
        await loadPendingUsers();
      } else {
        setError(result.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur lors du refus');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatLots = (lots: PendingUser['lots']): string => {
    if (!lots || lots.length === 0) return '';
    return lots.map(lot => `${LOT_TYPE_LABELS[lot.type]} n°${lot.numero}`).join(', ');
  };

  // Aucune inscription → ne rien afficher du tout
  if (!loading && pendingUsers.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      {/* Header compact */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          <h2 className="text-sm font-semibold text-gray-800">Inscriptions en attente</h2>
        </div>
        <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-0.5 rounded-full">
          {pendingUsers.length}
        </span>
      </div>

      {/* Messages */}
      {error && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
      )}
      
      {successMessage && (
        <div className="mx-4 mt-3 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">{successMessage}</div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {pendingUsers.map((pu) => (
            <div key={pu.id} className="group">
              {/* Ligne compacte */}
              <div 
                className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => setExpandedId(expandedId === pu.id ? null : pu.id)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {/* Chevron */}
                  <svg 
                    className={`w-4 h-4 text-gray-400 transition-transform ${expandedId === pu.id ? 'rotate-90' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  
                  {/* Nom + Email */}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-gray-900 text-sm">{pu.prenom} {pu.nom}</span>
                    <span className="text-gray-400 text-xs ml-2 hidden sm:inline">{pu.email}</span>
                  </div>
                  
                  {/* Lots (badge) */}
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded hidden md:inline">
                    {pu.lots?.length || 0} lot{(pu.lots?.length || 0) > 1 ? 's' : ''}
                  </span>
                  
                  {/* Date */}
                  <span className="text-xs text-gray-400 hidden lg:inline">
                    {formatDate(pu.createdAt)}
                  </span>
                </div>

                {/* Boutons */}
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={(e) => handleValidate(e, pu)}
                    disabled={processing === pu.id}
                    className={`p-1.5 rounded ${
                      processing === pu.id 
                        ? 'bg-gray-100 text-gray-400' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                    title="Valider"
                  >
                    {processing === pu.id ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={(e) => openRejectModal(e, pu)}
                    disabled={processing === pu.id}
                    className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100"
                    title="Refuser"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Détails (dépliés) */}
              {expandedId === pu.id && (
                <div className="px-4 pb-3 pt-1 bg-gray-50 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600">
                    <div>
                      <span className="text-gray-400">Email : </span>
                      <a href={`mailto:${pu.email}`} className="text-blue-600 hover:underline">{pu.email}</a>
                    </div>
                    <div>
                      <span className="text-gray-400">GSM : </span>
                      <a href={`tel:${pu.gsm}`} className="text-blue-600 hover:underline">{pu.gsm}</a>
                    </div>
                    <div>
                      <span className="text-gray-400">Étage : </span>
                      {ETAGE_LABELS[pu.etage] || pu.etage}
                    </div>
                    <div>
                      <span className="text-gray-400">Lots : </span>
                      {formatLots(pu.lots)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal refus */}
      {rejectModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Refuser l'inscription</h3>
            <p className="text-gray-600 mb-4">
              Refuser <strong>{selectedUser.prenom} {selectedUser.nom}</strong> ?
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motif (optionnel)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 text-sm"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setRejectModalOpen(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                disabled={processing !== null}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingUsersSection;
